import { memo } from "preact/compat";
import { useState, useEffect, useRef } from "preact/hooks";
import { Typography, Box } from "../../shared/ui";
import { getScoreColor } from "../../shared/utils/get-score-color";
import styles from "./LastCrashScores.module.scss";

export interface CrashScore {
  id: string;
  value: number;
  timestamp: number;
}

export interface LastCrashScoresProps {
  className?: string;
  scoresRef?: any;
  history?: number[];
}

export function LastCrashScoresComponent({
  className = "",
  scoresRef,
  history,
}: LastCrashScoresProps) {
  const [scores, setScores] = useState<CrashScore[]>([]);
  const [maxVisibleScores, setMaxVisibleScores] = useState(10);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  useEffect(() => {
    setMaxVisibleScores(50);
  }, []);

  const animTimerRef = useRef<number | null>(null);
  const prevHeadRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!history) return;

    const limited = history.slice(0, maxVisibleScores);
    const now = Date.now();
    const newScores: CrashScore[] = limited.map((value, idx) => ({
      id: `hist_${now}_${idx}_${value}`,
      value,
      timestamp: now - idx * 1000,
    }));

    const newHead = newScores[0]?.value;
    if (newHead !== prevHeadRef.current) {
      setIsAnimating(true);
      if (animTimerRef.current) {
        clearTimeout(animTimerRef.current);
      }
      animTimerRef.current = window.setTimeout(() => {
        setIsAnimating(false);
        animTimerRef.current = null;
      }, 500);
    }
    prevHeadRef.current = newHead;
    setScores(newScores);

    return () => {
      if (animTimerRef.current) {
        clearTimeout(animTimerRef.current);
        animTimerRef.current = null;
      }
    };
  }, [history, maxVisibleScores]);

  return (
    <Box
      className={`${styles.container} ${className}`}
      bg="dark"
      domRef={scoresRef}
    >
      <div
        ref={containerRef}
        className={`${styles.scoresContainer} ${isAnimating ? styles.animating : ""}`}
      >
        {scores.map((score, index) => (
          <div
            key={score.id}
            className={`${styles.scoreItem} ${
              index === 0 && isAnimating ? styles.newItem : ""
            }`}
          >
            <Typography
              variant="caption1"
              color={getScoreColor(score.value)}
              className={styles.scoreText}
            >
              {score.value.toFixed(2)}
            </Typography>
          </div>
        ))}
      </div>
    </Box>
  );
}
export const LastCrashScores = memo(LastCrashScoresComponent);
